import React from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  styled,
} from '@mui/material';
import {Body, Icon, designSystemToken} from '@lightricks/react-design-system';
import translate from '@/utils/translate';

type IconName = React.ComponentProps<typeof Icon>['name'];

const StyledAccordion = styled(Accordion)(() => ({
  'backgroundColor': designSystemToken('semantic.bg.primary'),
  'borderRadius': '16px !important',
  'margin': '0 auto 12px auto',
  'border': `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`,
  'boxShadow': 'none',
  '&.Mui-expanded': {
    margin: '0 auto 12px auto',
  },
  'width': '90%',
  '&:before': {
    display: 'none',
  },
}));

const SectionHeader = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '0px',
  width: '100%',
  justifyContent: 'space-between',
}));

const SectionHeaderContent = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}));

const DropdownArrow = styled(Icon, {
  shouldForwardProp: (prop: string) => prop !== 'expanded',
})<{expanded: boolean}>(({expanded}) => ({
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.2s ease-in-out',
  flexShrink: 0,
  marginLeft: 'auto',
  marginRight: '8px',
}));

const SectionContent = styled(Box)(() => ({
  paddingLeft: '24px',
  paddingRight: '24px',
  paddingBottom: '16px',
  paddingTop: '16px',
}));

interface AccordionSectionProps {
  sectionKey: string;
  title: string;
  iconName: IconName;
  expanded: boolean;
  onToggle: (section: string, expanded: boolean) => void;
  children: React.ReactNode;
}

function AccordionSection({
  sectionKey,
  title,
  iconName,
  expanded,
  onToggle,
  children,
}: AccordionSectionProps) {
  return (
    <StyledAccordion
      expanded={expanded}
      onChange={(_, isExpanded) => onToggle(sectionKey, isExpanded)}
    >
      <AccordionSummary expandIcon={null} sx={{height: '56px'}}>
        <SectionHeader>
          <SectionHeaderContent>
            <Box
              sx={{
                backgroundColor: designSystemToken('semantic.bg.tertiary'),
                width: 32,
                height: 32,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name={iconName} size="small" appearance="neutral" />
            </Box>
            <Body size="lg">{translate(title)}</Body>
          </SectionHeaderContent>
          <DropdownArrow
            name="Actions-Arrow-Down-Mini"
            size="medium"
            appearance="neutral"
            expanded={expanded}
            color={designSystemToken('semantic.bg.alpha-secondary')}
          />
        </SectionHeader>
      </AccordionSummary>
      <AccordionDetails sx={{padding: '0px'}}>
        <SectionContent>{children}</SectionContent>
      </AccordionDetails>
    </StyledAccordion>
  );
}

export default AccordionSection;
